import { Stack, TableCell } from "@mui/material";
import { flexRender, type Row } from "@tanstack/react-table";
import clsx from "clsx";
import { motion } from "framer-motion";
import React from "react";

interface HyperTableRowProps<Entity>
  extends Omit<
    React.ComponentProps<typeof motion.tr>,
    | "ref"
    | "initial"
    | "animate"
    | "transition"
    | "itemRef"
    | "data-state"
    | "onClick"
  > {
  row: Row<Entity>;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  itemRef?: React.LegacyRef<HTMLTableRowElement>;
  highlighted?: boolean;
}

export const HyperTableRow = <Entity extends any>(
  props: HyperTableRowProps<Entity>,
) => {
  const { row, onClick, itemRef, className, highlighted, ...rest } = props;

  const rowBackground = highlighted ? "rgba(5, 32, 76, 0.1)" : "white";

  return (
    <motion.tr
      ref={itemRef}
      initial={row.depth > 0 ? { opacity: 0.4, height: 0 } : {}}
      animate={row.depth > 0 ? { y: [-10, 0], opacity: 1, height: "auto" } : {}}
      transition={{ duration: 0.3 }}
      className={clsx("table-row", className)}
      data-state={row.getIsSelected() && "selected"}
      {...rest}
    >
      {row.getVisibleCells()?.map((cell, index, allCells) => {
        const isPinned = cell.column.getIsPinned();
        const leftOffset =
          isPinned && index > 0
            ? allCells
              .slice(0, index)
              .filter((col) => col.column.getIsPinned() === isPinned)
              .reduce((sum, col) => sum + (col.column.getSize() || 0), -100)
            : 0;

        const columnSize = cell.column.getSize();
        const style = isPinned
          ? {
            position: "sticky" as const,
            left: isPinned === "left" ? leftOffset : "unset",
            right: isPinned === "right" ? leftOffset : "unset",
            background: rowBackground,
            zIndex: 1,
            width: columnSize,
            minWidth: columnSize,
            maxWidth: columnSize,
          }
          : {
            width: columnSize,
            minWidth: columnSize,
            maxWidth: columnSize,
          };

        return (
          <TableCell
            sx={{
              ...style,
              padding: 0,
              overflow: "hidden",
              cursor:
                onClick && !cell.column.columnDef.preventRowClick
                  ? "pointer"
                  : "default",
            }}
            onClick={(e) =>
              !cell.column.columnDef.preventRowClick && onClick?.(e)
            }
            className={clsx(cell.column.columnDef.cellClassName, "table-cell")}
            key={cell.id}
          >
            <Stack
              className="cell-content"
              sx={{
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </Stack>
          </TableCell>
        );
      })}
    </motion.tr>
  );
};
